import { ComposeViewState } from 'owa-mail-compose-store';
import createAttachments from '../utils/createAttachments';
import findComposeViewStateById, { IdSource } from '../utils/findComposeViewStateById';
import { logUsage } from 'owa-analytics';
import { AttachmentFileType, CloudFile } from 'owa-attachment-file-types';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { orchestrator } from 'satcheljs';
import {
    createAttachmentFromLink,
    deleteLink,
    getSharingLink,
    LinkActionStatus,
    lazySetLinkActionStatus,
    SharingLinkInfo,
} from 'owa-link-data';

// set as a global variable so that it can be called sync in tryRecoverLink
// Will always be defined, since tryRecoverLink is only called as a callback for
// createAttachments inside of createAttachmentForLink, which sets it.
let setLinkActionStatus;

orchestrator(createAttachmentFromLink, async actionMessage => {
    const composeViewState: ComposeViewState = findComposeViewStateById(
        actionMessage.linkId,
        IdSource.Link
    );
    const sharingLink: SharingLinkInfo = getSharingLink(actionMessage.linkId);
    if (composeViewState && sharingLink) {
        //In Smime attash as a copy will show an error message.
        //In ComposeLinkView we currently don't know if we are Smime or not, so hiding the buttons
        // is non-trivial. Bug 49046 is tracking if we should enable this for Smime or not. Since the
        // error is thrown immidiately we do not bother changing the linkActionStatus.
        if (!isSmimeEnabledInViewState(composeViewState.smimeViewState)) {
            if (!setLinkActionStatus) {
                setLinkActionStatus = await lazySetLinkActionStatus.import();
            }
            setLinkActionStatus(actionMessage.linkId, LinkActionStatus.attachingAsACopy);
        }
        const cloudFile: CloudFile = createCloudFileFromSharingLink(
            sharingLink,
            actionMessage.providerId
        );
        createAttachments(
            [cloudFile],
            composeViewState,
            {
                isInline: false,
                isHiddenInline: false,
                shouldShare: false,
                downsellFromLink: true,
            },
            null /* processInlineImageBeforeUploadFunc */,
            null /* attachmentInfoBarAddHandler /*/,
            null /* attachmentInfoBarRemoveHandler /*/,
            null /* onAllAttachmentsProcessed /*/,
            (
                parentItemId: ClientItemId,
                attachmentId: ClientAttachmentId,
                attachment: AttachmentType
            ) =>
                tryDeleteLink(
                    actionMessage.linkId,
                    actionMessage.isTryAgain
                ) /* onAttachmentCreatedCallback */,
            (canceled: boolean) =>
                tryRecoverLink(
                    actionMessage.linkId,
                    canceled,
                    actionMessage.isTryAgain
                ) /* onAttachmentCanceledOrFailedCallback */,
            () => {
                logUsage('LinkAttachAsCopyTriedAgain');
                createAttachmentFromLink(
                    actionMessage.linkId,
                    actionMessage.providerId,
                    true /* isTryAgain */
                ); /* tryAgainCallback */
            }
        );
    }
});

function createCloudFileFromSharingLink(
    sharingLink: SharingLinkInfo,
    providerId: string
): CloudFile {
    return {
        fileType: AttachmentFileType.Cloud,
        name: sharingLink.fileName,
        size: null,
        providerId: providerId,
        providerType: AttachmentDataProviderType.OneDrivePro,
        location: sharingLink.sharingInfo.canonicalUrl,
        providerEndpointUrl: null,
        isFolder: false,
        fileId: sharingLink.linkId,
        fileParentId: null,
        thumbnailUrl: null,
        previewUrl: null,
        type: sharingLink.mimeType,
    };
}

// Deletes the link in the body after a copy is attached
async function tryDeleteLink(linkId: string, tryAgian: boolean) {
    // We put this in a set timeout in case the user cancels the attachment at the last second.
    // In this case we will get a cancel call back right after the attach call back, which will have changed the
    // actionStatus to let us know the attachment wasn't actually created. The window in which a user can do this
    // is larger than you would think.
    logUsage('linkAttachAsACopySuccess', { isTryAgain: tryAgian });
    setTimeout(() => {
        const sharingLink: SharingLinkInfo = getSharingLink(linkId);
        if (sharingLink && sharingLink.linkActionStatus === LinkActionStatus.attachingAsACopy) {
            deleteLink(linkId);
        }
    }, 100);
}

// Moves the link in the body back to its normal state after attachAsACopy fails or is canceled
function tryRecoverLink(linkId: string, canceled: boolean, tryAgain: boolean) {
    setLinkActionStatus(linkId, LinkActionStatus.none);
    if (canceled) {
        logUsage('linkAttachAsACopyCanceled', { isTryAGain: tryAgain, isCalendar: false });
    } else {
        logUsage('linkAttachAsACopyFailed', { isTryAgain: tryAgain, isCalendar: false });
    }
}
